import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, Scene } from "./types";

// Lesson: Policies -> Search and ask. Fixture `searchAndAsk`: the officer
// belongs to Riverton PD, which has uploaded a policy manual (PDF) and a
// municipal code (Word). The scene opens Policies, searches the documents
// list, opens the manual and uses the sections outline, then asks the chat a
// suggested question. The streamed answer is scripted by the stage
// (fixtures/policyChat.ts) and its first citation opens 300.7 on page 18.
const SECTION_300_7 = "e0000000-0000-4000-8000-000000000307";
const SECTION_310_2 = "e0000000-0000-4000-8000-000000000312";
const ROW = (id: string) => `#outline-${id} ${sel("policyOutlineRow")}`;
const CITE_READY = `${sel("chatCitation")}:not([disabled])`;

const scene: Scene = {
  id: "search-and-ask",
  title: "Search and ask",
  startRoute: "/cases",
  fixture: "searchAndAsk",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "Nobody wants to page through a policy manual at two in the morning. Once your department has uploaded its documents, a Policies item shows up in the sidebar. I'll open it.",
      actions: [
        { type: "waitFor", target: sel("policiesNav") },
        { type: "moveTo", target: sel("policiesNav") },
        { type: "spotlight", target: sel("policiesNav") },
        // "a Policies item" at about 6.4 s, "I'll open it" at 9.0 s.
        { type: "wait", ms: 8200 },
        { type: "spotlight", target: null },
        { type: "click", target: `${sel("policiesNav")} button` },
        { type: "waitFor", target: sel("policyDocRow") },
      ],
    },
    {
      id: "documents",
      narration:
        "The Documents tab lists everything your department uploaded, tagged by topic. To narrow the list, type a topic in the search box, like force. There's also an Ask button up here, which opens the chat in a side drawer.",
      actions: [
        { type: "moveTo", target: sel("policiesSearch") },
        // "like force" at about 8.3 s, "There's also an Ask button" at 9.1 s.
        { type: "wait", ms: 6800 },
        { type: "type", target: sel("policiesSearch"), text: "force", replace: true },
        { type: "wait", ms: 1200 },
        { type: "moveTo", target: sel("policiesAsk") },
        { type: "spotlight", target: sel("policiesAsk") },
        { type: "wait", ms: 3300 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "open",
      narration:
        "I'll open the Policy Manual. The page is on the left, and the sections outline is on the right. To jump somewhere, type in Find a section or topic, like pursuit.",
      actions: [
        { type: "moveTo", target: sel("policyDocRow") },
        // "I'll open the Policy Manual" at about 1 s, "like pursuit" at 8.8 s.
        { type: "wait", ms: 300 },
        { type: "click", target: sel("policyDocRow") },
        { type: "waitFor", target: sel("policyOutlineFilter") },
        { type: "wait", ms: 6200 },
        { type: "type", target: sel("policyOutlineFilter"), text: "pursuit", replace: true },
        { type: "waitFor", target: ROW(SECTION_310_2) },
      ],
    },
    {
      id: "section",
      narration:
        "Hover a section and its box lights up on the page, so you can see exactly where it sits. Click it, and the page jumps there. This button downloads the original document.",
      actions: [
        { type: "moveTo", target: ROW(SECTION_310_2) },
        // "Click it" at 5.3 s, "This button downloads" at 7.6 s.
        { type: "wait", ms: 4600 },
        { type: "click", target: ROW(SECTION_310_2) },
        { type: "wait", ms: 1600 },
        { type: "moveTo", target: sel("policyDownload") },
        { type: "spotlight", target: sel("policyDownload") },
        { type: "wait", ms: 2500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "ask",
      narration:
        "Now for the real questions. I'll go back to Policies and open the Chat tab. It starts with suggested questions built from your department's own documents. I'll pick this one.",
      actions: [
        // "go back to Policies" at 2.6 s, "Chat tab" at 4 s, "I'll pick this one" at 9.1 s.
        { type: "wait", ms: 1600 },
        { type: "moveTo", target: sel("policyBack") },
        { type: "wait", ms: 200 },
        { type: "click", target: sel("policyBack") },
        { type: "waitFor", target: sel("policiesTabChat") },
        { type: "wait", ms: 300 },
        { type: "click", target: sel("policiesTabChat") },
        { type: "waitFor", target: sel("chatSuggestion") },
        { type: "moveTo", target: sel("chatSuggestion") },
        { type: "wait", ms: 3800 },
        { type: "click", target: sel("chatSuggestion") },
      ],
    },
    {
      id: "answer",
      narration:
        "Watch the lines at the top. The assistant searches your policies, then reads the section it found. Then the answer streams in, and every claim carries a numbered citation.",
      actions: [{ type: "waitFor", target: CITE_READY, timeoutMs: 25000 }, { type: "wait", ms: 1500 }],
    },
    {
      id: "cite",
      narration: "Hover a number to preview the passage, or click it. I'll click the first one.",
      actions: [
        // "I'll click the first one" at 3.3 s.
        { type: "moveTo", target: CITE_READY },
        { type: "wait", ms: 2800 },
        { type: "click", target: CITE_READY },
        { type: "waitFor", target: ROW(SECTION_300_7) },
      ],
    },
    {
      id: "landed",
      narration:
        "And here is the section, on page 18 of the manual, selected in the outline and boxed on the page. The manual is still the authority, so read the wording here before you act.",
      actions: [
        { type: "moveTo", target: ROW(SECTION_300_7) },
        { type: "spotlight", target: ROW(SECTION_300_7) },
        { type: "wait", ms: 8500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "outro",
      narration:
        "One limit to remember. The chat only knows your department's documents, not state law or other agencies' policies. If the manual doesn't cover your question, it will tell you so.",
      actions: [{ type: "wait", ms: 2500 }],
    },
  ],
};

export default scene;
